import { Reveal } from "@/components/Reveal";
import { SectionHeading } from "@/components/SectionHeading";
import { SEO } from "@/components/SEO";
import { ExternalLink, Linkedin, Mail } from "lucide-react";

const team = [
  {
    name: "Thomas Codet",
    role: "Fondateur & Directeur",
    initials: "TC",
    photo: "/photo-thomas-codet.jpg",
    description: "Pilote la vision stratégique d'Intellix et accompagne les dirigeants de PME dans leur transformation numérique sécurisée.",
    linkedin: "https://www.linkedin.com/in/thomas-codet-b67ba9109/",
    email: "tcodet@intellix.fr",
  },
  {
    name: "Ludovic Chapet",
    role: "Cloud & IA Specialist",
    initials: "LC",
    photo: "/photo-ludovic-chapet.jpg",
    description: "Expert des environnements Microsoft Cloud et des solutions d'intelligence artificielle générative pour les entreprises.",
    linkedin: "https://www.linkedin.com/in/ludovic-chapet-67b9b62b7/",
    email: "lchapet@intellix.fr",
  },
  {
    name: "Franck Barthe",
    role: "Associé & Co-fondateur",
    initials: "FB",
    photo: "/photo-franck-barthe.jpg",
    description: "CEO & Fondateur de Kaizzen, expert Microsoft 365 et IA. Accompagne les entreprises dans leur stratégie de transformation numérique.",
    linkedin: "https://www.linkedin.com/in/franckbarthe/",
    email: null,
  },
  {
    name: "Josselin Dauton",
    role: "Associé & CTO",
    initials: "JD",
    photo: "/photo-josselin-dauton.jpg",
    description: "CTO & Co-fondateur de Kaizzen, spécialiste sécurité et optimisation technologique via Microsoft Cloud et Copilot.",
    linkedin: "https://www.linkedin.com/in/jdauton/",
    email: null,
  },
];

const partners = [
  {
    name: "Kaizzen",
    logo: "/logo-kaizzen-official.jpg",
    description: "Intégrateur Microsoft Cloud : Modern Work, Business Apps et Azure. Expertise déploiement et adoption pour les entreprises de 10 à 1 500 collaborateurs.",
    url: "https://kaizzen.fr",
  },
  {
    name: "Microsoft",
    logo: null,
    description: "Écosystème technologique de référence : Microsoft 365, Azure, Power Platform, Dynamics 365 et solutions de sécurité.",
    url: "https://www.microsoft.com/fr-fr",
  },
  {
    name: "DMC Defense",
    logo: "/logo-dmc-defense-official.webp",
    description: "SOC Microsoft Sentinel 24/7/365, tests d'intrusion, analyse forensique et remédiation immédiate. Partenaire cybersécurité d'Intellix.",
    url: "https://dmc-defense.com",
  },
  {
    name: "Be Cloud",
    logo: "/logo-becloud-official.jpg",
    description: "Partenaire Microsoft Gold et CSP, spécialiste de l'accompagnement Cloud pour les PME. Plus de 30 000 utilisateurs accompagnés.",
    url: "https://www.be-cloud.fr",
  },
  {
    name: "Lenoa",
    logo: "/logo-lenoa.png",
    description: "Modernisation d'infrastructure IT et transition vers le cloud Microsoft Azure. 3 designations Microsoft Solutions Partner sur Azure (Data & AI, Infrastructure, Digital & App Innovation).",
    url: "https://www.lenoa.net/",
  },
  {
    name: "Lenovo",
    logo: null,
    description: "Leader mondial des solutions matérielles : postes de travail, serveurs et solutions d'infrastructure pour les entreprises de toutes tailles.",
    url: "https://www.lenovo.com/fr/fr/",
  },
];

function MicrosoftLogo() {
  return (
    <svg viewBox="0 0 23 23" className="h-12 w-12" aria-hidden="true">
      <rect x="0" y="0" width="11" height="11" fill="#f25022" />
      <rect x="12" y="0" width="11" height="11" fill="#7fba00" />
      <rect x="0" y="12" width="11" height="11" fill="#00a4ef" />
      <rect x="12" y="12" width="11" height="11" fill="#ffb900" />
    </svg>
  );
}

function LenovoLogo() {
  return (
    <svg viewBox="0 0 200 40" className="h-8 w-auto" aria-hidden="true">
      <text x="0" y="32" fontFamily="Arial, sans-serif" fontSize="36" fontWeight="bold" fill="#E2231A" letterSpacing="-1">lenovo</text>
    </svg>
  );
}

export default function Equipe() {
  return (
    <div className="px-5 pb-24 pt-36 sm:px-8 lg:pt-44" data-testid="equipe-page">
      <SEO title="Équipe et Partenaires" description="Découvrez l'équipe Intellix et nos partenaires technologiques : Kaizzen, Microsoft, DMC Defense, Be Cloud. Experts Microsoft 365, Copilot, Purview et cybersécurité." path="/equipe" />
      <div className="mx-auto max-w-7xl">

        <SectionHeading
          eyebrow="Notre équipe"
          title="Des experts engagés à vos côtés."
          description="Une équipe resserrée, experte et réactive, dédiée à la réussite de votre transformation numérique."
        />

        <div className="mt-16 grid gap-6 md:grid-cols-2" data-testid="team-grid">
          {team.map((member) => (
            <Reveal key={member.name} delay={0.06}>
              <article className="flex h-full gap-6 rounded-3xl border border-border/70 bg-card/60 p-8 transition-transform duration-300 hover:-translate-y-1 hover:border-primary/50" data-testid={`team-member-${member.initials.toLowerCase()}`}>
                <div className="flex h-24 w-24 shrink-0 items-center justify-center rounded-2xl bg-primary/10 text-xl font-bold text-primary overflow-hidden">
                  {member.photo ? (
                    <img src={member.photo} alt={member.name} className="h-full w-full object-cover object-top" />
                  ) : (
                    member.initials
                  )}
                </div>
                <div className="flex flex-col">
                  <h3 className="font-display text-xl font-semibold tracking-tight">{member.name}</h3>
                  <p className="mt-1 text-sm font-semibold text-primary">{member.role}</p>
                  <p className="mt-4 flex-1 text-sm leading-relaxed text-muted-foreground">{member.description}</p>
                  <div className="mt-5 flex gap-4">
                    {member.linkedin && (
                      <a href={member.linkedin} target="_blank" rel="noreferrer" className="text-muted-foreground transition-colors hover:text-primary" aria-label={`LinkedIn de ${member.name}`}>
                        <Linkedin className="h-4 w-4" />
                      </a>
                    )}
                    {member.email && (
                      <a href={`mailto:${member.email}`} className="text-muted-foreground transition-colors hover:text-primary" aria-label={`Email de ${member.name}`}>
                        <Mail className="h-4 w-4" />
                      </a>
                    )}
                  </div>
                </div>
              </article>
            </Reveal>
          ))}
        </div>

        <div className="mt-28" data-testid="partners-section">
          <SectionHeading
            eyebrow="Nos partenaires"
            title="Un écosystème de confiance."
            description="Intellix s'appuie sur des partenaires technologiques de premier plan pour garantir l'excellence de chaque projet."
          />

          <div className="mt-16 grid gap-6 sm:grid-cols-2 xl:grid-cols-3" data-testid="partners-grid">
            {partners.map((partner, index) => (
              <Reveal key={partner.name} delay={index * 0.06}>
                <a
                  href={partner.url}
                  target="_blank"
                  rel="noreferrer"
                  className="group flex h-full flex-col rounded-3xl border border-border/70 bg-card/60 p-7 transition-transform duration-300 hover:-translate-y-1 hover:border-primary/50"
                  data-testid={`partner-${partner.name.toLowerCase().replace(/\s+/g, "-")}`}
                >
                  <div className="flex h-24 items-center justify-center rounded-2xl bg-black/40 p-4">
                    {partner.name === "Microsoft" ? (
                      <div className="rounded-xl bg-white p-2"><MicrosoftLogo /></div>
                    ) : partner.name === "Lenovo" ? (
                      <div className="rounded-xl bg-white px-4 py-2"><LenovoLogo /></div>
                    ) : (
                      <img src={partner.logo} alt={partner.name} className="h-14 w-auto object-contain rounded-xl bg-white p-2" />
                    )}
                  </div>
                  <h3 className="mt-6 font-display text-lg font-semibold tracking-tight">{partner.name}</h3>
                  <p className="mt-3 flex-1 text-sm leading-relaxed text-muted-foreground">{partner.description}</p>
                  <span className="mt-5 inline-flex items-center gap-2 text-xs font-semibold text-primary opacity-0 transition-opacity group-hover:opacity-100">
                    Visiter le site <ExternalLink className="h-3 w-3" />
                  </span>
                </a>
              </Reveal>
            ))}
          </div>
        </div>

      </div>
    </div>
  );
}
